import type { ClientErrorPostInput } from "./clientErrorTypes";

/**
 * Produktneutrale Basis für das Client-Error-Reporting.
 *
 * Der generische Teil (Einmal-Dedupe pro errorId, Kontext-Normalisierung,
 * Payload-Aufbau, Fire-and-Forget-POST, Fehlerschlucken) lebt hier. Produktlokal
 * bleiben Endpoint, optionale Auth-Header und ein optionaler Basis-Kontext.
 *
 * Mandant, Benutzer oder Account stehen bewusst nicht im Body: Der Server ermittelt sie
 * ausschließlich aus der Anmeldung (Cookie oder Header).
 */
export type ClientErrorReporterConfig = {
  /** POST-Ziel des Produkts (dokumentierter Client-Error-Endpoint-Vertrag). */
  endpoint: string;
  /** Auth-Header für Produkte ohne Cookie-Anmeldung (z. B. Bearer-Token und Mandantenkopf). */
  buildHeaders?: () => Record<string, string>;
  /**
   * Optionaler Basis-Kontext, der vor den Payload-Kontext gemergt wird
   * (z. B. eine globale `getClientErrorContext()`-Quelle).
   */
  getBaseContext?: () => ClientErrorPostInput["context"];
};

const normalizeClientErrorContext = (context: ClientErrorPostInput["context"]) =>
  Object.fromEntries(
    Object.entries(context || {}).map(([key, value]) => [key, value == null ? null : String(value)]),
  );

export function createClientErrorReporter({ endpoint, buildHeaders, getBaseContext }: ClientErrorReporterConfig) {
  const postedErrorIds = new Set<string>();

  return function postClientErrorOnce(payload: ClientErrorPostInput): void {
    try {
      if (!payload.errorId || postedErrorIds.has(payload.errorId)) return;
      postedErrorIds.add(payload.errorId);

      const context = normalizeClientErrorContext({
        ...(getBaseContext ? getBaseContext() : undefined),
        ...(payload.context || {}),
      });
      const body = {
        errorId: payload.errorId,
        message: payload.message || "Unknown error",
        context,
        source: payload.source ?? payload.sourceKind ?? "frontend",
        sourceKind: payload.sourceKind ?? null,
        stack: payload.stack ?? null,
        componentStack: payload.componentStack ?? null,
        url: payload.url ?? (typeof window !== "undefined" ? window.location.href : null),
        userAgent: payload.userAgent ?? (typeof navigator !== "undefined" ? navigator.userAgent : null),
        timestamp: payload.timestamp ?? new Date().toISOString(),
      };

      void fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(buildHeaders ? buildHeaders() : undefined) },
        body: JSON.stringify(body),
      }).catch(() => {});
    } catch {
      // Fehler-Logging darf nie weitere sichtbare Fehler auslösen.
    }
  };
}
